import { ActivatedRoute, Data, ParamMap } from '@angular/router';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';

/**
 * Filter route param by name
 */
export const filterRouteParam = (paramName: string) => {
  return (source: Observable<ParamMap>) =>
    source.pipe(
      filter((params) => params.has(paramName)),
      map((params) => params.get(paramName) as string),
      distinctUntilChanged(),
    );
};

export const filterRouteParamWithDefault = (
  paramName: string,
  defaultValue: string,
) => {
  return (source: Observable<ParamMap>) =>
    source.pipe(
      map((params) =>
        params.has(paramName)
          ? (params.get(paramName) as string)
          : defaultValue,
      ),
      distinctUntilChanged(),
    );
};

/**
 * Filter route params by multiple names
 */
export const filterRouteParams = (paramNames: Array<string>) => {
  return (source: Observable<ParamMap>) =>
    source.pipe(
      filter((params) =>
        paramNames.every((paramName) => params.has(paramName)),
      ),
      map((params) =>
        paramNames.map((paramName) => params.get(paramName) as string),
      ),
      distinctUntilChanged(),
    );
};

/**
 * Filter data attribute by name
 */
export const filterDataParam = <T>(paramName: string) => {
  return (source: Observable<Data>) =>
    source.pipe(
      filter((params) => params[paramName]),
      map((params) => params[paramName] as T),
      distinctUntilChanged(),
    );
};

export const paramMapWithName = (
  paramName: string,
  activatedRoutes: Array<ActivatedRoute>,
): ParamMap | null => {
  let paramMapWhichContainsName: ParamMap | null = null;

  if (!activatedRoutes || activatedRoutes.length === 0) {
    return null;
  }

  activatedRoutes.forEach((activatedRoute) => {
    activatedRoute.paramMap.subscribe((paramMap) => {
      if (paramMap.has(paramName)) {
        paramMapWhichContainsName = paramMap;
      } else {
        paramMapWhichContainsName = paramMapWithName(
          paramName,
          activatedRoute.children,
        );
      }
    });
  });

  return paramMapWhichContainsName;
};

export const filterChildrenDataParam = <T>(
  paramName: string,
  activatedRoutes: Array<ActivatedRoute>,
): Observable<T | null> => {
  return new Observable<T | null>((subscriber) => {
    const checkChildren = (routes: Array<ActivatedRoute>): boolean => {
      return routes.some((route) => {
        if (
          Object.prototype.hasOwnProperty.call(route.snapshot.data, paramName)
        ) {
          subscriber.next(route.snapshot.data[paramName] as T);
          return true;
        }
        if (route.children.length > 0) {
          return checkChildren(route.children);
        }
        return false;
      });
    };

    if (!checkChildren(activatedRoutes)) {
      subscriber.next(null);
    }

    return () => subscriber.complete();
  });
};
